import type { CreateFormData } from "@/components/create/types";
import type {
  AgentPhase,
  AgentResponse,
  AgentSideEffect,
  AgentWidget,
  PresetOption,
} from "@/types/createAgent";
import {
  DESIGN_BRIEF_REDUCER_FIELDS,
  mergeDesignBriefPatch,
} from "./designBriefReducer";
import { filterPresetsForState } from "./filterPresetsForState";
import { resolveAgentPhase } from "./phase";

/** A different active session requires the server's explicit replacement fact. */
export function projectAgentSessionId(
  currentSessionId: string | null | undefined,
  response: Pick<AgentResponse, "sessionId" | "sessionReplaced">
): string | null {
  return response.sessionId && (!currentSessionId || response.sessionReplaced)
    ? response.sessionId
    : (currentSessionId ?? null);
}

/** Protect the approved front before platform-specific attachment processing. */
export function prepareAgentExtractedData(
  response: {
    extractedData?: AgentResponse["extractedData"];
    sideEffects: readonly AgentSideEffect[];
  },
  currentFormData: Partial<CreateFormData>
): Partial<CreateFormData> & Record<string, unknown> {
  const processedData = { ...response.extractedData };
  const isModifyResponse = response.sideEffects.some(
    effect =>
      effect.type === "generate_image" && effect.generateType === "modify"
  );
  if (
    currentFormData.frontApproved === true &&
    "prompt" in processedData &&
    !isModifyResponse
  )
    delete processedData.prompt;
  return processedData;
}

/**
 * Filters extracted data from LLM responses to prevent overwriting existing
 * form values with empty strings or empty arrays. LLMs often return the full
 * schema template with empty values, which would wipe the form.
 * Also protects frontApproved/backApproved from being overwritten with false.
 */
function filterExtractionData<T extends Record<string, unknown>>(
  data: T,
  existingFormData?: Partial<CreateFormData>
): Partial<T> {
  const filtered: Partial<T> = {};
  for (const key of Object.keys(data) as Array<keyof T>) {
    const value = data[key];
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    // Never overwrite frontApproved/backApproved with false from extraction
    if (
      (key === "frontApproved" || key === "backApproved") &&
      value === false &&
      existingFormData &&
      existingFormData[key as keyof CreateFormData] === true
    ) {
      continue;
    }
    filtered[key] = value;
  }
  return filtered;
}

function reconcileExtractedDesignBriefData(
  currentFormData: CreateFormData,
  processedData: Partial<CreateFormData> & Record<string, unknown>,
  userMessage: string
): Partial<CreateFormData> & Record<string, unknown> {
  const reducerResult = mergeDesignBriefPatch(currentFormData, processedData, {
    userMessage,
  });
  const reconciled = { ...processedData };
  const reducerFields = new Set<keyof CreateFormData>();

  for (const field of DESIGN_BRIEF_REDUCER_FIELDS) {
    if (field in processedData) {
      reducerFields.add(field);
    }
  }

  for (const change of [
    ...reducerResult.replacements,
    ...reducerResult.removals,
  ]) {
    reducerFields.add(change.field);
  }

  for (const field of reducerFields) {
    const processedValue = processedData[field];
    if (
      (typeof processedValue === "string" &&
        processedValue.trim().length === 0) ||
      (Array.isArray(processedValue) && processedValue.length === 0)
    ) {
      // Empty values emitted from the server are durable brief tombstones, not
      // missing extraction data. Replacing them with the old local form value
      // would immediately resurrect a field the customer just removed.
      continue;
    }
    (reconciled as Record<string, unknown>)[field] = reducerResult.data[field];
  }

  return reconciled;
}

/**
 * Apply the same extraction/removal semantics after browser attachment downloads
 * or local JSON processing. No I/O, stores, identity changes or side effects.
 */
export function projectAgentFormPatch(
  currentFormData: CreateFormData,
  processedData: Partial<CreateFormData> & Record<string, unknown>,
  userMessage: string
): Partial<CreateFormData> & Record<string, unknown> {
  const removalPreview = mergeDesignBriefPatch(currentFormData, processedData, {
    userMessage,
  });
  const filtered = filterExtractionData(
    processedData,
    currentFormData
  ) as Partial<CreateFormData> & Record<string, unknown>;
  for (const removal of removalPreview.removals)
    (filtered as Record<string, unknown>)[removal.field] =
      removalPreview.data[removal.field];
  return reconcileExtractedDesignBriefData(
    currentFormData,
    filtered,
    userMessage
  );
}

/** Project accepted response controls against the already-reconciled draft. */
export function projectAgentResponseControls(input: {
  response: {
    phase: AgentPhase;
    presets?: PresetOption[];
    widgets?: AgentWidget[];
  };
  fallbackPhase: AgentPhase;
  formState: Partial<CreateFormData>;
  hasFrontImage: boolean;
  hasBackImage: boolean;
}) {
  const phase = resolveAgentPhase({
    requestedPhase: input.response.phase,
    fallbackPhase: input.fallbackPhase,
    formState: input.formState,
    hasFrontImage: input.hasFrontImage,
    hasBackImage: input.hasBackImage,
  });
  const presets = filterPresetsForState({
    presets: Array.isArray(input.response.presets)
      ? input.response.presets
      : [],
    phase,
    formState: input.formState,
    hasFrontImage: input.hasFrontImage,
    hasBackImage: input.hasBackImage,
    frontApproved: input.formState.frontApproved === true,
  });
  return {
    phase,
    presets,
    widgets: Array.isArray(input.response.widgets)
      ? input.response.widgets
      : [],
  };
}
